import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Icon,
  InlineStack,
  ProgressBar,
  Text,
} from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import {
  useCompleteOnboardingStep,
  useDismissOnboarding,
  useOnboarding,
} from '../../hooks/useOnboarding';
import type { OnboardingStepKey } from '../../api/onboarding';

interface Props {
  /** Called externally when a step links to another page (e.g. /audits). */
  onNavigate?: (step: OnboardingStepKey) => void;
}

export function OnboardingChecklist({ onNavigate }: Props) {
  const { data, isLoading } = useOnboarding();
  const completeStep        = useCompleteOnboardingStep();
  const dismiss             = useDismissOnboarding();

  if (isLoading || !data || data.is_dismissed || data.is_complete) {
    return null;
  }

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              Get started with GrowthPilot AI
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {data.completed_count} of {data.total_steps} steps complete
            </Text>
          </BlockStack>

          <Button
            variant="plain"
            tone="critical"
            onClick={() => dismiss.mutate()}
            loading={dismiss.isPending}
          >
            Dismiss
          </Button>
        </InlineStack>

        <ProgressBar progress={data.progress_percent} tone="primary" size="small" />

        {/* Steps */}
        <BlockStack gap="300">
          {data.steps.map((step) => (
            <InlineStack key={step.key} gap="300" blockAlign="start">
              <Box paddingBlockStart="050">
                {step.is_complete ? (
                  <Icon source={CheckIcon} tone="success" accessibilityLabel="Completed" />
                ) : (
                  <>
                    <Box
                      width="20px"
                      minHeight="20px"
                      borderWidth="025"
                      borderColor="border"
                      borderRadius="full"
                    />
                    <Text as="span" visuallyHidden>
                      Not completed
                    </Text>
                  </>
                )}
              </Box>

              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center">
                  <Text
                    as="p"
                    variant="bodyMd"
                    fontWeight={step.is_complete ? 'regular' : 'semibold'}
                    tone={step.is_complete ? 'subdued' : undefined}
                  >
                    {step.label}
                  </Text>
                  {step.is_auto_complete && (
                    <Badge tone="success" size="small">Auto</Badge>
                  )}
                </InlineStack>

                {!step.is_complete && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {step.description}
                  </Text>
                )}

                {!step.is_complete && !step.is_auto_complete && (
                  <Box paddingBlockStart="100">
                    <Button
                      size="slim"
                      onClick={() => {
                        onNavigate?.(step.key as OnboardingStepKey);
                        completeStep.mutate(step.key as OnboardingStepKey);
                      }}
                      loading={completeStep.isPending && completeStep.variables === step.key}
                    >
                      Mark as done
                    </Button>
                  </Box>
                )}
              </BlockStack>
            </InlineStack>
          ))}
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
